import { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox } from '@nazka/nazka.mapframe.components';
import maplibregl from 'maplibre-gl';
import { createRoot } from 'react-dom/client';

import ConstraintsLegendControl from '@App/components/Map/controls/ConstraintsLegendControl/ConstraintsLegendControl.jsx';

import ConstraintsPopup from '../../components/ConstraintsPopup/ConstraintsPopup.jsx';

import { layerColors, layers, sources } from './contraints.layers';

import './constraints.css';

const s3BucketUrl = import.meta.env.VITE_S3_BUCKET_URL;

const inputLayersOptions = [
  { label: 'EHUBt', value: 'constraint-1' },
  { label: 'Warmtevraagscore', value: 'constraint-2' },
];

const obstructionOptions = [
  { label: 'Obstructieslaag', value: 'obstruction-1' },
];

const inputLayerLegends = [
  {
    label: 'EHUBt',
    optionValue: 'constraint-1',
    layerId: layers.ehubt.id,
    colors: layerColors.ehubt,
  },
  {
    label: 'Warmtevraagscore',
    optionValue: 'constraint-2',
    layerId: layers.warmtevraag.id,
    colors: layerColors.warmtevraag,
  },
  {
    label: 'Obstructielaag',
    optionValue: 'obstruction-1',
    layerId: layers.obstructionPolygon.id,
    opacityLayerIds: [layers.obstructionPolygon.id, layers.obstructionLine.id],
  },
];

function Constraints({ map }) {
  const legendControlRef = useRef(null);
  const [activeInputLayers, setActiveInputLayers] = useState([inputLayersOptions[0].value]);
  const [activeObstructions, setActiveObstructions] = useState([]);
  const [layerOpacities, setLayerOpacities] = useState({
    [layers.ehubt.id]: 0.8,
    [layers.warmtevraag.id]: 0.8,
    [layers.obstructionPolygon.id]: 0.8,
    [layers.obstructionLine.id]: 0.8,
  });

  const toggleOption = (setOptions, value, checked) => {
    setOptions(options => checked
      ? [...options, value]
      : options.filter(option => option !== value));
  };

  const updateLayerOpacity = useCallback((layerIds, value) => {
    const opacity = Number(value);
    setLayerOpacities(current => layerIds.reduce(
      (updatedOpacities, layerId) => ({ ...updatedOpacities, [layerId]: opacity }),
      current,
    ));
    layerIds.forEach((layerId) => {
      const layer = map?.getLayer(layerId);
      if (layer) {
        map.setPaintProperty(layerId, layer.type === 'line' ? 'line-opacity' : 'fill-opacity', opacity);
      }
    });
  }, [map]);

  useEffect(() => {
    if (!map) return undefined;

    const legendControl = new ConstraintsLegendControl();
    legendControlRef.current = legendControl;
    map.addControl(legendControl, 'bottom-right');

    return () => {
      map.removeControl(legendControl);
      legendControlRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    const activeLegends = inputLayerLegends.filter(legend => (
      activeInputLayers.includes(legend.optionValue)
    )).concat(inputLayerLegends.filter(legend => (
      legend.optionValue === 'obstruction-1'
      && activeObstructions.includes(legend.optionValue)
    )));

    legendControlRef.current?.update({
      legends: activeLegends,
      layerOpacities,
      onOpacityChange: updateLayerOpacity,
    });
  }, [activeInputLayers, activeObstructions, layerOpacities, updateLayerOpacity]);

  useEffect(() => {
    if (!map || !s3BucketUrl) return undefined;

    Object.values(sources).forEach((source) => {
      if (!map.getSource(source.id)) {
        map.addSource(source.id, {
          type: source.type,
          url: `pmtiles://${s3BucketUrl}/${source.pmtilesFile}`,
        });
      }
    });

    const styleLayers = map.getStyle().layers;
    const roadLabelsIndex = styleLayers.findIndex(layer => layer.id === 'Road labels');
    const layerAfterRoadLabelsId = roadLabelsIndex >= 0
      ? styleLayers[roadLabelsIndex + 1]?.id
      : undefined;

    [
      layers.warmtevraag,
      layers.hoveredWarmtevraag,
      layers.obstructionPolygon,
      layers.obstructionLine,
      layers.ehubt,
      layers.hoveredEhubt,
    ].forEach((layer) => {
      if (!map.getLayer(layer.id)) {
        map.addLayer(layer, layerAfterRoadLabelsId);
      }
    });

    let hoveredEhubtId;
    let hoveredWarmtevraagId;

    const setFeatureHoverState = (source, sourceLayer, featureId, hover) => {
      if (featureId !== undefined && featureId !== null) {
        map.setFeatureState({ source, sourceLayer, id: featureId }, { hover });
      }
    };

    const handleEhubtMouseMove = (event) => {
      const featureId = event.features?.[0]?.id;
      if (featureId === undefined || featureId === null || hoveredEhubtId === featureId) return;

      setFeatureHoverState(sources.ehubt.id, 'EHUBt', hoveredEhubtId, false);
      setFeatureHoverState(sources.ehubt.id, 'EHUBt', featureId, true);
      hoveredEhubtId = featureId;
    };

    const handleEhubtMouseLeave = () => {
      setFeatureHoverState(sources.ehubt.id, 'EHUBt', hoveredEhubtId, false);
      hoveredEhubtId = undefined;
    };

    const handleWarmtevraagMouseMove = (event) => {
      const featureId = event.features?.[0]?.id;
      if (featureId === undefined || featureId === null || hoveredWarmtevraagId === featureId) return;

      setFeatureHoverState(sources.statSector.id, 'Stat_sector', hoveredWarmtevraagId, false);
      setFeatureHoverState(sources.statSector.id, 'Stat_sector', featureId, true);
      hoveredWarmtevraagId = featureId;
    };

    const handleWarmtevraagMouseLeave = () => {
      setFeatureHoverState(sources.statSector.id, 'Stat_sector', hoveredWarmtevraagId, false);
      hoveredWarmtevraagId = undefined;
    };

    const interactiveLayerIds = [layers.ehubt.id, layers.warmtevraag.id];
    const handleInteractiveMouseMove = (event) => {
      const features = map.queryRenderedFeatures(event.point, {
        layers: interactiveLayerIds,
      });
      map.getCanvas().style.cursor = features.length > 0 ? 'pointer' : '';
    };

    let popup;
    let popupRoot;

    const closePopup = () => {
      popupRoot?.unmount();
      popupRoot = undefined;
      popup?.remove();
      popup = undefined;
    };

    const showFeaturePopup = (event, scoreProperty, colors, fallbackTitle) => {
      const feature = event.features?.[0];
      if (!feature) return;

      const properties = feature.properties || {};
      const score = Number(properties[scoreProperty]);
      const normalizedScore = Number.isInteger(score) && score >= 0 && score < colors.length ? score : 0;
      const title = properties.Naam || properties.naam || properties.name || fallbackTitle;
      const popupContainer = document.createElement('div');

      closePopup();
      popupRoot = createRoot(popupContainer);
      popupRoot.render(
        <ConstraintsPopup
          color={colors[normalizedScore]}
          onClose={closePopup}
          score={normalizedScore}
          title={title}
        />,
      );
      popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 12,
      })
        .setLngLat(event.lngLat)
        .setDOMContent(popupContainer)
        .addTo(map);
    };

    const handleEhubtClick = event => showFeaturePopup(
      event,
      'EHUBt_score',
      layerColors.ehubt,
      'EHUBt',
    );

    const handleWarmtevraagClick = event => showFeaturePopup(
      event,
      'warmtevraag_score',
      layerColors.warmtevraag,
      'Warmtevraagscore',
    );

    map.on('mousemove', layers.ehubt.id, handleEhubtMouseMove);
    map.on('mouseleave', layers.ehubt.id, handleEhubtMouseLeave);
    map.on('mousemove', layers.warmtevraag.id, handleWarmtevraagMouseMove);
    map.on('mouseleave', layers.warmtevraag.id, handleWarmtevraagMouseLeave);
    map.on('mousemove', handleInteractiveMouseMove);
    map.on('click', layers.ehubt.id, handleEhubtClick);
    map.on('click', layers.warmtevraag.id, handleWarmtevraagClick);

    return () => {
      map.off('mousemove', layers.ehubt.id, handleEhubtMouseMove);
      map.off('mouseleave', layers.ehubt.id, handleEhubtMouseLeave);
      map.off('mousemove', layers.warmtevraag.id, handleWarmtevraagMouseMove);
      map.off('mouseleave', layers.warmtevraag.id, handleWarmtevraagMouseLeave);
      map.off('mousemove', handleInteractiveMouseMove);
      map.off('click', layers.ehubt.id, handleEhubtClick);
      map.off('click', layers.warmtevraag.id, handleWarmtevraagClick);
      setFeatureHoverState(sources.ehubt.id, 'EHUBt', hoveredEhubtId, false);
      setFeatureHoverState(sources.statSector.id, 'Stat_sector', hoveredWarmtevraagId, false);
      map.getCanvas().style.cursor = '';
      closePopup();
      Object.values(layers).forEach(({ id }) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      Object.values(sources).forEach(({ id }) => {
        if (map.getSource(id)) map.removeSource(id);
      });
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;

    const visibleLayerIds = new Set([
      ...(activeInputLayers.includes('constraint-1')
        ? [layers.ehubt.id, layers.hoveredEhubt.id]
        : []),
      ...(activeInputLayers.includes('constraint-2')
        ? [layers.warmtevraag.id, layers.hoveredWarmtevraag.id]
        : []),
      ...(activeObstructions.includes('obstruction-1')
        ? [layers.obstructionPolygon.id, layers.obstructionLine.id]
        : []),
    ]);

    Object.values(layers).forEach(({ id }) => {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', visibleLayerIds.has(id) ? 'visible' : 'none');
      }
    });
  }, [activeInputLayers, activeObstructions, map]);

  useEffect(() => {
    Object.entries(layerOpacities).forEach(([layerId, opacity]) => {
      if (map?.getLayer(layerId)) {
        const layer = map.getLayer(layerId);
        map.setPaintProperty(layerId, layer.type === 'line' ? 'line-opacity' : 'fill-opacity', opacity);
      }
    });
  }, [layerOpacities, map]);

  return (
    <div className="constraints">
      <fieldset className="constraints__options">
        <legend>Inputlagen</legend>
        {inputLayersOptions.map(option => (
          <Checkbox
            checked={activeInputLayers.includes(option.value)}
            key={option.value}
            name="input-layer"
            onInputChange={event => toggleOption(setActiveInputLayers, option.value, event.target.checked)}
            text={option.label}
          />
        ))}
      </fieldset>
      <fieldset className="constraints__options">
        <legend>Obstructies</legend>
        {obstructionOptions.map(option => (
          <Checkbox
            checked={activeObstructions.includes(option.value)}
            key={option.value}
            name="obstruction"
            onInputChange={event => toggleOption(setActiveObstructions, option.value, event.target.checked)}
            text={option.label}
          />
        ))}
      </fieldset>
    </div>
  );
}

export default Constraints;
